// Pure timing helpers for the dev-only per-turn debug badge. Client-measured
// from the SSE status phase boundaries (never trust server timing).

import type { StatusPhase } from './types';

export type PhaseMark = { phase: StatusPhase; at: number };

export type TimingBreakdown = {
  /** Milliseconds spent in each phase (recurring phases summed). */
  spans: Partial<Record<StatusPhase, number>>;
  /** Time to first assistant-message token, in ms (null if none). */
  firstToken: number | null;
  /** Total wall-clock, in ms. */
  total: number;
};

/**
 * Turn ordered phase marks into per-phase durations. Each mark starts a phase
 * that runs until the next mark (or `end` for the last). Recurring phases sum.
 */
export const computeBreakdown = (
  marks: PhaseMark[],
  t0: number,
  firstTokenAt: number | null,
  end: number = performance.now(),
): TimingBreakdown => {
  const spans: Partial<Record<StatusPhase, number>> = {};
  for (let i = 0; i < marks.length; i += 1) {
    const start = marks[i].at;
    const stop = i + 1 < marks.length ? marks[i + 1].at : end;
    const phase = marks[i].phase;
    spans[phase] = (spans[phase] ?? 0) + (stop - start);
  }
  return {
    spans,
    firstToken: firstTokenAt != null ? firstTokenAt - t0 : null,
    total: end - t0,
  };
};

export const fmtMs = (ms: number | null | undefined): string => {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
};

export const fmtUsd = (v: number | null | undefined): string => {
  if (v == null) return '—';
  if (v === 0) return '$0';
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
};
